/**
 * Learning Center API: the Team tab's queues and lookups (plan
 * docs/plans/lms-plan.md Appendix A "Sign-offs and external certificates" and
 * "Certificates and the training log"; 7.3, 7.4, 7.5, 10.4; 18.15, 18.19,
 * 18.21, 18.22; WP-34's and WP-35's routes, WP-45's client). Typed calls over
 * services/api; nothing under src/lms imports axios (2.3a). Every route here
 * is an lms admin route.
 *
 *   GET  /admin/signoff-requests?status=open|done|cancelled&limit=&offset=
 *        -> { items: [request + learner {id, full_name, email, deleted}, course,
 *                     observer, last_signoff], page }   oldest first (a queue)
 *   POST /admin/signoffs {user_id, course_id, checklist: [{id, result, note?}],
 *        outcome, notes?, password}  -> 201 { signoff, completion | null }
 *   GET  /admin/external-certs?status=pending|accepted|rejected&limit=&offset=
 *        -> { items: [row + holder {id, full_name, email, deleted}], page }
 *   POST /admin/external-certs/:id/review {decision, note?, password}
 *        -> { external_cert, completion | null }
 *   GET  /admin/certificates/verify?code=LMS-YYYY-NNNNNN
 *        -> { certificate, holder, course, status: valid | expired | superseded,
 *             completed_at, valid_until, superseded_at }  (404 for an unknown code
 *             or a holder the admin does not reach, 18.19)
 *
 * THE PASSWORD POSTS (7.3, R20; 18.22). A sign-off and an external-certificate
 * review re-check the admin's credential on the server
 * (signatureService.validateSignatureCredentials); a wrong one is 401
 * LMS_REAUTH_FAILED. Both posts pass the axios config { _retry: true }: the
 * shared client refreshes the session and REPLAYS a request that answered
 * 401, which would post the password twice and, when the refresh fails,
 * clear the stored site and user. The flag opts these two posts out of that.
 * The password travels in the body once and is never stored or logged here.
 *
 * The other Team calls live beside their owners and are imported, never
 * duplicated: the person summary and the directory (api/directory.ts, WP-43),
 * waive and unwaive (api/assignments.ts, WP-43), the matrix and the reports
 * (api/reports.ts), a certificate or an external certificate file
 * (api/completions.ts, api/externalCerts.ts) and the training log
 * (api/certificates.ts).
 */
import api from '../../services/api';
import type { LmsList } from './client';
import type { CompletionRow } from './completions';
import type { ExternalCert } from './externalCerts';

const enc = encodeURIComponent;

/** The axios config every password post carries (see the file header, 18.22). */
function passwordPostConfig(): Record<string, unknown> {
  return { _retry: true };
}

/** A person as an admin queue names them (records/common.js personRef): leavers stay listed, flagged. */
export interface QueuePerson {
  id: string;
  full_name: string | null;
  email: string | null;
  deleted: boolean;
}

// ---------------------------------------------------------------------------
// The attestation statements (7.3: fixed per kind; the server stores its own text)
// ---------------------------------------------------------------------------

export const COMPETENCY_STATEMENT = "I observed this person perform the listed steps and attest to the results recorded.";
export const EXTERNAL_ACCEPT_STATEMENT = "I reviewed this certificate and accept it as evidence of the named requirement.";

/** The server's bounds (records/signoffs.js, records/externalCerts.js). */
export const SIGNOFF_NOTES_MAX = 4000;
export const SIGNOFF_STEP_NOTE_MAX = 1000;
export const REVIEW_NOTE_MAX = 2000;
/** records/common.js parsePaging: at most this many rows per page. */
export const QUEUE_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Competency sign-offs (7.4, R18)
// ---------------------------------------------------------------------------

export type SignoffRequestStatus = 'open' | 'done' | 'cancelled';
export type ChecklistResult = 'met' | 'not_met' | 'na';
export type SignoffOutcome = 'competent' | 'not_yet';

/** The latest observation of a (learner, course) pair. */
export interface SignoffObservationSummary {
  id: string;
  outcome: SignoffOutcome;
  observed_at: string;
  notes: string | null;
  observer_id: string;
}

/** One GET /admin/signoff-requests item. */
export interface AdminSignoffRequest {
  id: string;
  user_id: string;
  course_id: string;
  /** A named observer, or null for "any admin" (7.4). */
  observer_id: string | null;
  status: SignoffRequestStatus;
  signoff_id: string | null;
  requested_at: string;
  created_at?: string;
  learner: QueuePerson | null;
  course: { id: string; title: string; slug: string } | null;
  observer: { id: string; full_name: string | null; email: string | null } | null;
  last_signoff: SignoffObservationSummary | null;
}

export interface QueueQuery<S extends string> {
  status?: S;
  limit?: number;
  offset?: number;
}

function queueParams<S extends string>(q: QueueQuery<S>): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (q.status) params.status = q.status;
  if (q.limit !== undefined) params.limit = Math.min(q.limit, QUEUE_MAX_LIMIT);
  if (q.offset !== undefined) params.offset = q.offset;
  return params;
}

/** GET /api/lms/admin/signoff-requests (open by default on the server). */
export async function listSignoffQueue(q: QueueQuery<SignoffRequestStatus> = {}): Promise<LmsList<AdminSignoffRequest>> {
  const response = await api.get<LmsList<AdminSignoffRequest>>('/api/lms/admin/signoff-requests', { params: queueParams(q) });
  return response.data;
}

export interface SignoffStepInput {
  id: string;
  result: ChecklistResult;
  note?: string | null;
}

export interface RecordSignoffInput {
  user_id: string;
  course_id: string;
  /** Every step of the current version's competency_checklist, each marked (7.4). */
  checklist: SignoffStepInput[];
  outcome: SignoffOutcome;
  notes?: string | null;
  password: string;
}

/** An lms_competency_signoffs row (5.5). */
export interface SignoffRow {
  id: string;
  user_id: string;
  course_id: string;
  course_version_id: string;
  observer_id: string;
  checklist: Array<{ id: string; text: string; result: ChecklistResult; note: string | null }>;
  outcome: SignoffOutcome;
  observed_at: string;
  notes: string | null;
}

export interface RecordSignoffResult {
  signoff: SignoffRow;
  /** The competency completion when the passed quiz was already on record (7.4), else null. */
  completion: CompletionRow | null;
}

/**
 * POST /api/lms/admin/signoffs with the credential re-check (7.3, 7.4). A
 * step note is sent only when it has text; notes only when set.
 */
export async function recordSignoff(input: RecordSignoffInput): Promise<RecordSignoffResult> {
  const checklist = input.checklist.map((step) => {
    const out: Record<string, string> = { id: step.id, result: step.result };
    const note = (step.note ?? '').trim();
    if (note) out.note = note;
    return out;
  });
  const body: Record<string, unknown> = {
    user_id: input.user_id,
    course_id: input.course_id,
    checklist,
    outcome: input.outcome,
    password: input.password,
  };
  const notes = (input.notes ?? '').trim();
  if (notes) body.notes = notes;
  const response = await api.post<RecordSignoffResult>('/api/lms/admin/signoffs', body, passwordPostConfig());
  return response.data;
}

// ---------------------------------------------------------------------------
// External certificates (7.5, R17)
// ---------------------------------------------------------------------------

export type ExternalCertQueueStatus = 'pending' | 'accepted' | 'rejected';
export type ReviewDecision = 'accepted' | 'rejected';

/** One GET /admin/external-certs item: the owner's row plus the holder. */
export interface AdminExternalCert extends ExternalCert {
  uploaded_by?: string | null;
  holder: QueuePerson | null;
}

/** GET /api/lms/admin/external-certs (pending by default on the server). */
export async function listExternalCertQueue(q: QueueQuery<ExternalCertQueueStatus> = {}): Promise<LmsList<AdminExternalCert>> {
  const response = await api.get<LmsList<AdminExternalCert>>('/api/lms/admin/external-certs', { params: queueParams(q) });
  return response.data;
}

export interface ReviewExternalCertInput {
  decision: ReviewDecision;
  note?: string | null;
  password: string;
}

export interface ReviewExternalCertResult {
  external_cert: ExternalCert;
  /** The external completion an acceptance against a requirement wrote (7.5), else null. */
  completion: CompletionRow | null;
}

/** POST /api/lms/admin/external-certs/:id/review with the credential re-check (7.3, 7.5). */
export async function reviewExternalCert(certId: string, input: ReviewExternalCertInput): Promise<ReviewExternalCertResult> {
  const body: Record<string, unknown> = { decision: input.decision, password: input.password };
  const note = (input.note ?? '').trim();
  if (note) body.note = note;
  const response = await api.post<ReviewExternalCertResult>(
    `/api/lms/admin/external-certs/${enc(certId)}/review`,
    body,
    passwordPostConfig()
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Verify a certificate by its code (10.4, R24)
// ---------------------------------------------------------------------------

export type CertificateVerifyStatus = 'valid' | 'expired' | 'superseded';

export interface VerifiedCertificate {
  certificate: {
    id: string;
    completion_id: string;
    code: string;
    issued_at: string;
    holder_user_id: string;
    holder_name: string;
    course_title: string;
    course_slug: string;
    version_no: number;
    completed_at: string;
    valid_until?: string | null;
  };
  holder: { full_name: string | null; email: string | null };
  course: { title: string; slug: string; version_no: number };
  /** superseded wins over expired (10.1, 18.19). */
  status: CertificateVerifyStatus;
  completed_at: string;
  valid_until: string | null;
  superseded_at: string | null;
}

/** 10.2: LMS-{YYYY}-{NNNNNN}, the number zero-padded to six digits and growing past 999999 (18.19). */
export const CERTIFICATE_CODE_RE = /^LMS-\d{4}-\d{6,}$/;

/** The code as printed: trimmed and upper-cased (the server normalizes the same way). */
export function normalizeCertificateCode(raw: string): string {
  return raw.trim().toUpperCase();
}

/** GET /api/lms/admin/certificates/verify?code= */
export async function verifyCertificate(code: string): Promise<VerifiedCertificate> {
  const response = await api.get<VerifiedCertificate>('/api/lms/admin/certificates/verify', {
    params: { code: normalizeCertificateCode(code) },
  });
  return response.data;
}
